/**
 * IFRAP-AI.fr - Home Page (Public)
 * Version editoriale - Observatoire economique
 * 
 * Features:
 * 1. Hero - Observatoire economique IFRAP
 * 2. Live counters (temps reel)
 * 3. Tableau de bord de crise (10 KPI a la une) with source/date
 * 4. Source suggestion modal (users) / Data update modal (admins)
 * 5. Blocs thematiques
 * 6. Newsletter
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  HEADLINE_KPIS,
  ALL_BLOCS,
  KPI_STATUS_LABELS,
  KPI_ORIGIN,
} from '../../data/editorialKpis';
import { useOpenDataKpis } from '../../hooks/useOpenDataKpis';
import { useAuth } from '../../context/AuthContext';
import LiveCounters from '../../components/LiveCounters';
import InstallAppPrompt from '../../components/InstallAppPrompt';
import { ExternalLink, Send, Edit3, X, AlertTriangle, ChevronRight } from 'lucide-react';
import api from '../../api/client';

const ADMIN_EMAILS = ['jbgiraud@mac.com', 'averdier@ifrap.org'];

// ============================================================================
// COMPOSANTS REUTILISABLES
// ============================================================================

const StatusBadge = ({ status }) => {
  const config = KPI_STATUS_LABELS[status];
  if (!config) return null;
  return (
    <span className={`text-xs px-2 py-0.5 rounded border ${config.color}`} data-testid={`status-badge-${status}`}>
      {config.label}
    </span>
  );
};

const OriginIndicator = ({ origin }) => {
  const isIfrap = origin === KPI_ORIGIN.BOOK;
  return (
    <span 
      className={`inline-flex items-center justify-center w-4 h-4 rounded-sm text-[10px] font-bold ${
        isIfrap 
          ? 'bg-[#000091]/10 text-[#000091] border border-[#000091]/30' 
          : 'bg-gray-100 text-gray-500 border border-gray-200'
      }`}
      title={isIfrap ? "Indicateur IFRAP" : "Complément Emergent"}
    >
      {isIfrap ? 'I' : 'E'}
    </span>
  );
};

const TrendIndicator = ({ trend }) => {
  const icons = {
    up: (
      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    ),
    down: (
      <svg className="w-4 h-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
      </svg>
    ),
    stable: (
      <svg className="w-4 h-4 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14" />
      </svg>
    ),
  };
  return icons[trend] || null;
};

// ============================================================================
// SUGGEST SOURCE MODAL
// ============================================================================
const SuggestSourceModal = ({ kpi, onClose }) => {
  const [sourceUrl, setSourceUrl] = useState('');
  const [sourceName, setSourceName] = useState('');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/kpis/suggest-source', {
        kpi_id: kpi.id,
        source_url: sourceUrl,
        source_name: sourceName,
        comment,
      });
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="suggest-source-modal">
        {success ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Send className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg text-gray-900">Merci !</h3>
            <p className="text-sm text-gray-500 mt-1">Votre suggestion a été enregistrée.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-[#000091]">Proposer une source</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              KPI : <strong>{kpi.label}</strong> (actuellement MOCK)
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">URL de la source *</label>
                <input
                  type="url"
                  required
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  placeholder="https://www.insee.fr/..."
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-[#000091] focus:border-transparent"
                  data-testid="suggest-source-url"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Nom de la source</label>
                <input
                  type="text"
                  value={sourceName}
                  onChange={(e) => setSourceName(e.target.value)}
                  placeholder="Ex: INSEE, Eurostat..."
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Commentaire</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Précision sur la donnée..."
                  rows={2}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#000091] text-white py-2 rounded font-medium text-sm hover:bg-[#000091]/90 disabled:opacity-50"
                data-testid="suggest-source-submit"
              >
                {submitting ? 'Envoi...' : 'Envoyer la suggestion'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// ADMIN UPDATE MODAL
// ============================================================================
const AdminUpdateModal = ({ kpi, onClose }) => {
  const [newValue, setNewValue] = useState(kpi.value);
  const [source, setSource] = useState(kpi.source || '');
  const [sourceUrl, setSourceUrl] = useState(kpi.sourceUrl || '');
  const [date, setDate] = useState(kpi.date || '');
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post('/api/kpis/update-data', {
        kpi_id: kpi.id,
        new_value: newValue,
        unit: kpi.unit,
        source,
        source_url: sourceUrl,
        date,
        comment,
      });
      setSuccess(true);
      setTimeout(onClose, 2000);
    } catch (err) {
      alert(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-update-modal">
        {success ? (
          <div className="text-center py-8">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Edit3 className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="font-semibold text-lg text-gray-900">Mis à jour !</h3>
            <p className="text-sm text-gray-500 mt-1">La donnée a été enregistrée.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-lg text-[#E1000F]">Mettre à jour le KPI</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              KPI : <strong>{kpi.label}</strong>
            </p>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Valeur *</label>
                  <input
                    type="text"
                    required
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    data-testid="admin-update-value"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 block mb-1">Date *</label>
                  <input
                    type="text"
                    required
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    placeholder="2025, T3 2025..."
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    data-testid="admin-update-date"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Source *</label>
                <input
                  type="text"
                  required
                  value={source}
                  onChange={(e) => setSource(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  data-testid="admin-update-source"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">URL source</label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 block mb-1">Commentaire</label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={2}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#E1000F] text-white py-2 rounded font-medium text-sm hover:bg-[#E1000F]/90 disabled:opacity-50"
                data-testid="admin-update-submit"
              >
                {submitting ? 'Mise à jour...' : 'Valider la mise à jour'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT KPI PRINCIPAL (A LA UNE)
// ============================================================================

const HeadlineKpiCard = ({ kpi, isExpanded, onToggle, isAdmin, onSuggestSource, onAdminUpdate }) => {
  const { editorial } = kpi;
  const isStale = kpi.status === 'stale';
  const isMock = kpi.status === 'mock';
  const showSuggest = isMock || kpi.status === 'estimate' || kpi.status === 'to_validate';
  
  return (
    <div 
      className={`bg-white border-l-4 rounded-lg shadow-sm hover:shadow-md transition-all ${
        isStale 
          ? 'border-l-red-500 ring-2 ring-red-300 animate-pulse' 
          : kpi.criticality === 'critical' ? 'border-l-[#E1000F]' : 'border-l-[#000091]'
      }`}
      data-testid={`headline-kpi-${kpi.id}`}
    >
      {/* Stale data warning */}
      {isStale && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-1.5 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5 text-red-500" />
          <span className="text-xs font-semibold text-red-600 uppercase">
            Donnée périmée
          </span>
          {isAdmin && (
            <button
              onClick={(e) => { e.stopPropagation(); onAdminUpdate(kpi); }}
              className="ml-auto text-xs bg-red-600 text-white px-2 py-0.5 rounded hover:bg-red-700"
              data-testid={`admin-update-btn-${kpi.id}`}
            >
              Mettre à jour
            </button>
          )}
        </div>
      )}

      {/* Header */}
      <div className="p-4 cursor-pointer" onClick={onToggle}>
        <div className="flex items-start justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">{kpi.label}</span>
            <OriginIndicator origin={kpi.origin} />
          </div>
          <div className="flex items-center gap-1.5">
            <StatusBadge status={kpi.status} />
            {showSuggest && (
              <button
                onClick={(e) => { e.stopPropagation(); onSuggestSource(kpi); }}
                className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded border border-orange-200 hover:bg-orange-200"
                data-testid={`suggest-source-btn-${kpi.id}`}
                title="Proposer une source pour cette donnée"
              >
                + Source
              </button>
            )}
          </div>
        </div>
        
        {/* Valeur principale */}
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold text-[#000091]">{kpi.value}</span>
          <span className="text-lg text-gray-500">{kpi.unit}</span>
        </div>
        
        {/* Sous-valeur et tendance */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-sm text-gray-400">{kpi.subValue}</span>
          <div className="flex items-center gap-1">
            <TrendIndicator trend={kpi.trend} />
            <span className="text-xs text-gray-500">{kpi.trendLabel}</span>
          </div>
        </div>

        {/* SOURCE + DATE */}
        <div className="flex items-center gap-1 mt-2 pt-2 border-t border-gray-100">
          <span className="text-[10px] text-gray-400">
            Source : {kpi.source || 'Non renseignée'}
          </span>
          {kpi.sourceUrl && (
            <a
              href={kpi.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="text-[#000091] hover:text-[#000091]/70"
            >
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
          <span className="text-[10px] text-gray-300 mx-1">|</span>
          <span className="text-[10px] text-gray-400">
            {kpi.date || '—'}
          </span>
        </div>
      </div>
      
      {/* Contenu editorial expandable */}
      {isExpanded && editorial && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          <blockquote className="text-sm text-gray-600 italic mb-3 pl-3 border-l-2 border-[#E1000F]">
            "{editorial.quote}"
          </blockquote>
          <div className="space-y-2 text-xs">
            <div className="flex gap-2">
              <span className="font-semibold text-[#000091] whitespace-nowrap">Important :</span>
              <span className="text-gray-600">{editorial.whyImportant}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold text-[#000091] whitespace-nowrap">Lecture IFRAP :</span>
              <span className="text-gray-600">{editorial.ifrapView}</span>
            </div>
            <div className="flex gap-2">
              <span className="font-semibold text-[#000091] whitespace-nowrap">A surveiller :</span>
              <span className="text-gray-600">{editorial.watchFor}</span>
            </div>
          </div>

          {/* Admin update button in expanded view */}
          {isAdmin && (isStale || isMock) && (
            <div className="mt-3 pt-3 border-t border-gray-200">
              <button
                onClick={(e) => { e.stopPropagation(); onAdminUpdate(kpi); }}
                className="flex items-center gap-1.5 text-xs bg-[#000091] text-white px-3 py-1.5 rounded hover:bg-[#000091]/90"
                data-testid={`admin-update-expanded-${kpi.id}`}
              >
                <Edit3 className="w-3 h-3" />
                Mettre à jour cette donnée
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Indicateur expandable */}
      <div className="px-4 pb-2 text-center cursor-pointer" onClick={onToggle}>
        <span className="text-xs text-gray-400">
          {isExpanded ? '\u25B2 Réduire' : '\u25BC Analyse IFRAP'}
        </span>
      </div>
    </div>
  );
};

// ============================================================================
// COMPOSANT BLOC THEMATIQUE (with source/date)
// ============================================================================

const BLOC_TO_THEME = {
  'dette_deficit': 'finances',
  'depense_publique': 'finances',
  'industrie': 'emploi',
  'travail': 'emploi',
  'normes': 'finances',
  'securite': null,
};

const ThematicBloc = ({ bloc }) => {
  const themeSlug = BLOC_TO_THEME[bloc.id];
  return (
    <div 
      className="bg-white rounded-lg shadow-sm border border-gray-100"
      data-testid={`bloc-${bloc.id}`}
    >
      <div 
        className="px-4 py-3 border-b border-gray-100"
        style={{ borderLeftWidth: '4px', borderLeftColor: bloc.color }}
      >
        <h3 className="font-semibold text-gray-800">{bloc.title}</h3>
        <p className="text-xs text-gray-500">{bloc.subtitle}</p>
      </div>
      
      {bloc.cautionNote && (
        <div className="px-4 py-2 bg-yellow-50 border-b border-yellow-100">
          <p className="text-xs text-yellow-700">
            <AlertTriangle className="w-3 h-3 inline mr-1" />
            {bloc.cautionNote}
          </p>
        </div>
      )}
      
      <div className="p-4 space-y-3">
        {bloc.kpis.map((kpi) => (
          <div key={kpi.id}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <OriginIndicator origin={kpi.origin} />
                <span className="text-sm text-gray-700">{kpi.label}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-[#000091]">
                  {kpi.value}<span className="text-gray-400 text-xs ml-1">{kpi.unit}</span>
                </span>
                <StatusBadge status={kpi.status} />
              </div>
            </div>
            {/* Source line for bloc KPIs */}
            <div className="flex items-center gap-1 ml-6 mt-0.5">
              <span className="text-[10px] text-gray-400">{kpi.source || ''}</span>
              {kpi.sourceUrl && (
                <a href={kpi.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#000091]">
                  <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {bloc.insight && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-600 italic">
            "{bloc.insight}"
          </p>
          {themeSlug && (
            <Link
              to={`/theme/${themeSlug}`}
              className="inline-flex items-center gap-1 text-xs font-medium mt-2 hover:underline"
              style={{ color: bloc.color }}
            >
              En savoir plus <ChevronRight className="w-3 h-3" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPOSANT PRINCIPAL - HOME PAGE
// ============================================================================

const HomePage = () => {
  const [expandedKpi, setExpandedKpi] = useState(null);
  const [suggestModal, setSuggestModal] = useState(null);
  const [updateModal, setUpdateModal] = useState(null);
  const { mergeWithStaticKpis, lastRefresh, hasRealData, loading } = useOpenDataKpis();
  const { user, isAuthenticated } = useAuth();
  
  const isAdmin = isAuthenticated && user?.email && ADMIN_EMAILS.includes(user.email);
  
  const enrichedKpis = mergeWithStaticKpis(HEADLINE_KPIS);
  
  const toggleKpi = (kpiId) => {
    setExpandedKpi(expandedKpi === kpiId ? null : kpiId);
  };
  
  const formatLastUpdate = (date) => {
    if (!date) return null;
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <div className="bg-[#F8F9FA]">
      {/* PWA Install Prompt */}
      <InstallAppPrompt />
      
      {/* HERO */}
      <section className="bg-gradient-to-br from-[#000091] via-[#000091] to-[#1a1a6e] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-[#E1000F]/20 border border-[#E1000F]/40 rounded-full px-4 py-1 mb-6">
              <span className="w-2 h-2 bg-[#E1000F] rounded-full animate-pulse"></span>
              <span className="text-sm font-medium text-[#E1000F]">Observatoire de crise</span>
            </div>
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight">
              Observatoire économique<br />
              <span className="text-[#E1000F]">Fondation IFRAP</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Tableau de bord de la situation économique française.<br />
              Chiffres vérifiés, analyses IFRAP, urgences identifiées.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#indicateurs"
                className="bg-white text-[#000091] px-6 py-3 font-medium rounded hover:bg-gray-100 transition-colors"
                data-testid="cta-indicateurs"
              >
                Voir les indicateurs
              </a>
              <a
                href="https://www.ifrap.org"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/40 text-white px-6 py-3 font-medium rounded hover:bg-white/10 transition-colors"
              >
                Études IFRAP
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* LEGENDE STATUTS */}
      <section className="bg-white border-b border-gray-200 py-3">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop: single row */}
          <div className="hidden md:flex flex-wrap items-center justify-center gap-4 text-xs">
            <span className="text-gray-500">Statut des données :</span>
            <StatusBadge status="real" />
            <StatusBadge status="mock" />
            <StatusBadge status="estimate" />
            <StatusBadge status="stale" />
            <span className="text-gray-400 ml-4">|</span>
            <span className="text-gray-500">Origine :</span>
            <span className="flex items-center gap-1">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-[10px] font-bold bg-[#000091]/10 text-[#000091] border border-[#000091]/30">I</span>
              IFRAP
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">E</span>
              Complément
            </span>
            {lastRefresh && (
              <>
                <span className="text-gray-400 ml-4">|</span>
                <span className="text-gray-500 flex items-center gap-1">
                  {loading && <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>}
                  {hasRealData && <span className="text-green-600">&#x25cf;</span>}
                  Mise à jour : {formatLastUpdate(lastRefresh)}
                </span>
              </>
            )}
          </div>
          
          {/* Mobile: stacked compact */}
          <div className="md:hidden text-xs space-y-2">
            <div className="flex items-center justify-center gap-2 flex-wrap">
              <StatusBadge status="real" />
              <StatusBadge status="estimate" />
              <span className="flex items-center gap-1">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-[10px] font-bold bg-[#000091]/10 text-[#000091] border border-[#000091]/30">I</span>
                IFRAP
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-sm text-[10px] font-bold bg-gray-100 text-gray-500 border border-gray-200">E</span>
                Complément
              </span>
            </div>
            {lastRefresh && (
              <div className="text-center text-gray-400">
                {hasRealData && <span className="text-green-600 mr-1">●</span>}
                MAJ : {formatLastUpdate(lastRefresh)}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* COMPTEURS TEMPS REEL */}
      <LiveCounters />

      {/* TABLEAU DE BORD DE CRISE */}
      <section id="indicateurs" className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header - Stack on mobile */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-[#000091]">Tableau de bord de crise</h2>
              <p className="text-gray-500 text-xs md:text-sm mt-1">
                Indicateurs critiques — Cliquez pour l'analyse
                {hasRealData && <span className="ml-2 text-green-600 font-medium">(données réelles)</span>}
              </p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link
                to="/comparaison"
                className="text-xs sm:text-sm font-medium text-[#000091] border border-[#000091] px-2 sm:px-3 py-1 sm:py-1.5 rounded hover:bg-[#000091]/5 transition-colors whitespace-nowrap"
                data-testid="comparison-link"
              >
                France vs Europe
              </Link>
              <span className="w-2 h-2 sm:w-3 sm:h-3 bg-[#E1000F] rounded-full animate-pulse flex-shrink-0"></span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {enrichedKpis.map((kpi) => (
              <HeadlineKpiCard
                key={kpi.id}
                kpi={kpi}
                isExpanded={expandedKpi === kpi.id}
                onToggle={() => toggleKpi(kpi.id)}
                isAdmin={isAdmin}
                onSuggestSource={(k) => {
                  if (!isAuthenticated) {
                    alert('Connectez-vous pour proposer une source.');
                    return;
                  }
                  setSuggestModal(k);
                }}
                onAdminUpdate={(k) => setUpdateModal(k)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* BLOCS THEMATIQUES */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#000091]">Analyse par thème</h2>
            <p className="text-gray-500 text-sm mt-1">
              Sources : IFRAP, INSEE, Eurostat, data.gouv.fr
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {ALL_BLOCS.map((bloc) => (
              <ThematicBloc key={bloc.id} bloc={bloc} />
            ))}
          </div>
        </div>
      </section>

      {/* NOS THÉMATIQUES */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-xl font-bold text-[#000091] mb-6">Nos thématiques</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {[
              { slug: 'finances', label: 'Finances publiques', color: '#E1000F' },
              { slug: 'sante', label: 'Santé', color: '#E1000F' },
              { slug: 'retraites', label: 'Retraites', color: '#000091' },
              { slug: 'education', label: 'Éducation', color: '#059669' },
              { slug: 'energie', label: 'Énergie', color: '#D97706' },
              { slug: 'emploi', label: 'Emploi', color: '#059669' },
              { slug: 'collectivites', label: 'Collectivités', color: '#7C3AED' },
            ].map(t => (
              <Link
                key={t.slug}
                to={`/theme/${t.slug}`}
                className="flex items-center justify-center text-center px-3 py-3 rounded-lg text-sm font-medium text-white transition-opacity hover:opacity-80"
                style={{ backgroundColor: t.color }}
                data-testid={`theme-link-${t.slug}`}
              >
                {t.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CE QU'EN DIT L'IFRAP */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-[#000091] mb-4">Ce qu'en dit l'IFRAP</h2>
            <div className="prose prose-sm max-w-none text-gray-600">
              <p>
                La Fondation IFRAP, think tank dédié à l'analyse des politiques publiques, 
                alerte depuis des années sur la trajectoire insoutenable des finances publiques françaises.
              </p>
              <p>
                Les chiffres présentés sur cette page illustrent les conclusions de nos études : 
                la France dépense trop, taxe trop, réglemente trop, et produit de moins en moins.
              </p>
              <p>
                <strong>Notre conviction :</strong> seule une réforme profonde de l'État et une baisse 
                significative de la dépense publique permettront de redresser la situation.
              </p>
            </div>
            <div className="mt-4 flex flex-wrap gap-4">
              <Link
                to="/publications"
                className="inline-flex items-center gap-2 bg-[#000091] text-white px-4 py-2 rounded-lg font-medium text-sm hover:bg-[#000091]/90 transition-colors"
              >
                <ExternalLink className="w-4 h-4" />
                Voir nos publications
              </Link>
              <a
                href="https://www.ifrap.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#000091] font-medium text-sm hover:underline flex items-center gap-1"
              >
                Découvrir toutes nos études →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-12 bg-[#000091] text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-semibold mb-4">
            Restez informé
          </h2>
          <p className="text-gray-300 mb-6">
            Recevez nos analyses et alertes sur la situation économique française.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
            <input
              type="email"
              placeholder="Votre email"
              className="flex-1 px-4 py-3 rounded text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#E1000F]"
              data-testid="newsletter-email"
            />
            <button
              type="button"
              className="bg-[#E1000F] text-white px-6 py-3 font-medium rounded hover:bg-[#E1000F]/90 transition-colors whitespace-nowrap"
              data-testid="newsletter-submit"
            >
              S'abonner
            </button>
          </div>
          <p className="text-xs text-gray-400 mt-4">
            En vous inscrivant, vous acceptez de recevoir la newsletter IFRAP.
          </p>
        </div>
      </section>

      {/* MODALS */}
      {suggestModal && <SuggestSourceModal kpi={suggestModal} onClose={() => setSuggestModal(null)} />}
      {updateModal && <AdminUpdateModal kpi={updateModal} onClose={() => setUpdateModal(null)} />}
    </div>
  );
};

export default HomePage;
